<template>
  <div v-if="service && service.service" class="flex items-center justify-between leading-none py-3">
    <div>
      <p class="text-sm font-medium text-gray-800 mb-1 dark:text-gray-100">
        {{ service.service.title }}
      </p>
      <p class="text-sm text-gray-600 dark:text-gray-300">
        {{ employee.displayName }}
      </p>
    </div>

    <div>
      <USelectMenu
        v-if="items && items.length"
        v-model="_discount"
        :items="items"
        label-key="label"
        :by="compareItems"
        :placeholder="$t('components.reservation.create.discountService.select')"
        :filter-fields="['label']"
        class="min-w-52"
      >
        <template #default="{ modelValue: selected }">
          <div v-if="selected && !isNoneItem(selected)" class="flex items-center justify-between w-full h-full text-sm text-gray-800 dark:text-gray-100">
            <p class="text-sm text-gray-800 dark:text-gray-100">
              {{ labelForDiscount(optionValue(selected)) }}
            </p>

            <o-chip size="xs" variant="info" flat class="mr-2">
              {{ isVoucher(optionValue(selected)) ? $t('general.promoCode') : $t('general.pass') }}
            </o-chip>
          </div>
        </template>

        <template #item="{ item }">
          <div v-if="isVoucher(optionValue(item))" class="w-full flex items-center justify-between">
            <div>
              <p class="text-gray-800 dark:text-gray-100">
                {{ optionValue(item).name }}
              </p>
              <p class="text-gray-600 dark:text-gray-300">
                {{ optionValue(item).discountAmount }}
              </p>
            </div>

            <o-chip size="xs" variant="success" flat>
              {{ $t('general.promoCode') }}
            </o-chip>
          </div>

          <div v-else-if="isCustomerPass(optionValue(item))" class="w-full flex items-center justify-between">
            <div>
              <p class="text-gray-800 dark:text-gray-100">
                {{ optionValue(item).pass?.name }}
              </p>
              <p class="text-gray-600 dark:text-gray-300">
                {{ optionValue(item).infoLabel }}
              </p>
            </div>

            <o-chip size="xs" variant="success" flat>
              {{ $t('general.pass') }}
            </o-chip>
          </div>

          <div v-else class="w-full flex items-center justify-between">
            <div>
              <p class="text-gray-800 dark:text-gray-100">
                {{ item.label }}
              </p>
            </div>
          </div>
        </template>
      </USelectMenu>

      <p v-else class="text-sm text-gray-600 dark:text-gray-300">
        {{ $t('components.reservation.create.discountService.noDiscounts') }}
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Voucher } from '@/_api/models/voucher'
import { CustomerPass } from '@/_api/models/customerPass'

const { $i18n } = useNuxtApp()

interface Props {
  voucher?: object | null
  customerPass?: object | null
  staging: Record<string, any>
  service: Record<string, any>
  employee: Record<string, any>
}

interface Emits {
  (e: 'update:voucher', value: any): void
  (e: 'update:customerPass', value: any): void
}

const props = withDefaults(defineProps<Props>(), {
  voucher: null,
  customerPass: null,
})

const emit = defineEmits<Emits>()

const hasAutoSelected = ref(false)

const noneItem = computed(() => ({
  _sentinel: true,
  label: $i18n.t('general.none') as string,
  value: null,
}))

function isNoneItem(item: any): boolean {
  return item?._sentinel === true || optionValue(item) === null
}

function optionValue(item: any): any {
  return item && Object.prototype.hasOwnProperty.call(item, 'value') ? item.value : item
}

function isVoucher(item: any): boolean {
  return item instanceof Voucher || item?.idVoucher != null
}

function isCustomerPass(item: any): boolean {
  return item instanceof CustomerPass || item?.idCustomerPass != null
}

function labelForDiscount(item: any): string {
  if (isVoucher(item)) {
    return item.name
  }

  if (isCustomerPass(item)) {
    return item.pass?.name || item.infoLabel
  }

  return item?.label || item?.name || ''
}

function compareItems(a: any, b: any): boolean {
  const av = optionValue(a)
  const bv = optionValue(b)

  if (av === bv) return true
  if (av === null && bv === null) return true
  if (!av || !bv) return false
  if (isVoucher(av) && isVoucher(bv)) return av.idVoucher === bv.idVoucher
  if (isCustomerPass(av) && isCustomerPass(bv)) return av.idCustomerPass === bv.idCustomerPass
  return false
}

const _discount = computed({
  get () {
    const value = props.voucher || props.customerPass
    return items.value.find((item: any) => compareItems(item, value)) || noneItem.value
  },
  set (value) {
    const discount = optionValue(value)

    if (isNoneItem(value)) {
      emit('update:voucher', null)
      emit('update:customerPass', null)
    } else if (isVoucher(discount)) {
      emit('update:voucher', discount)
      emit('update:customerPass', null)
    } else if (isCustomerPass(discount)) {
      emit('update:voucher', null)
      emit('update:customerPass', discount)
    } else {
      emit('update:voucher', null)
      emit('update:customerPass', null)
    }
  },
})

const items = computed(() => {
  const service = props.service

  const vouchers = service.vouchers || []
  const passes = service.passes || []
  const discounts = [...vouchers, ...passes]
  const selectedDiscount = props.voucher || props.customerPass

  // A selected final-use pass is omitted from the next validation response
  // because it has already been reserved for this service. Keep that current
  // selection visible so it can still be reviewed or removed before saving.
  if (selectedDiscount && !discounts.some((discount: any) => compareItems(discount, selectedDiscount))) {
    discounts.push(selectedDiscount)
  }

  const opts = discounts.map((discount: any) => ({
    value: discount,
    label: labelForDiscount(discount),
  }))

  if (opts.length > 0) {
    return [noneItem.value, ...opts]
  }

  return opts
})

function autoSelectPass () {
  if (props.customerPass || props.voucher || hasAutoSelected.value) {
    return
  }

  const passes = props.service?.passes || []

  const preselectedPass = passes.find(
    (customerPass: any) => customerPass.pass?.preselectAtConfirmationDefault,
  )

  if (preselectedPass) {
    _discount.value = preselectedPass
    hasAutoSelected.value = true
  }
}

watch(() => props.service.passes, () => {
  autoSelectPass()
}, { immediate: true })
</script>
